import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { useAiChat } from '@factorialco/f0-react'
import { Pencil, Delete } from '@factorialco/f0-react/icons/app'
import { CompensationPlanDetailRouteFragment } from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'
import { recurrenceLabel } from 'modules/incentives/lib/recurrence'
import { dateBackendToUI, stringToDate } from 'modules/core/lib/dates'
import { getCurrentPeriod } from 'modules/incentives/lib/period'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useNavigation } from 'modules/core/hooks/useNavigation'

interface CompensationPlanDetailHeaderProps {
  plan: CompensationPlanDetailRouteFragment
}

export const CompensationPlanDetailHeader = ({ plan }: CompensationPlanDetailHeaderProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [searchParams] = useSearchParams()
  const { enabled: aiEnabled, sendMessage, setOpen: setAiChatOpen } = useAiChat()

  const periods = plan.compensationPlanPeriods
  const selectedPeriodId = searchParams.get('periodId')
  const currentPeriod = getCurrentPeriod(periods)
  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId) ?? currentPeriod

  if (!selectedPeriod?.startsOn || !selectedPeriod?.endsOn)
    throw new Error('Selected period is missing start or end date')

  const startsOnDate = stringToDate(selectedPeriod?.startsOn, 'backend')
  const endsOnDate = stringToDate(selectedPeriod?.endsOn, 'backend')
  const currentDate = moment()

  const disabled = startsOnDate.isAfter(currentDate) || endsOnDate.isAfter(currentDate)

  const openOne = (message: string) => {
    if (aiEnabled) {
      setAiChatOpen(true)
      sendMessage(message)
    }
  }

  const otherActions = [
    {
      label: i18n.t('incentives.detail.update_plan.label'),
      icon: Pencil,
      onClick: () =>
        openOne(i18n.t('incentives.detail.update_plan.one_prompt', { planName: plan.name })),
    },
    ...(plan.status === 'draft'
      ? [
          {
            label: i18n.t('incentives.detail.delete_plan.label'),
            icon: Delete,
            critical: true as const,
            onClick: () =>
              openOne(i18n.t('incentives.detail.delete_plan.one_prompt', { planName: plan.name })),
          },
        ]
      : []),
  ]

  return (
    <ResourceHeader
      title={plan.name}
      primaryAction={{
        label: i18n.t('incentives.detail.send_to_compensation.label'),
        onClick: () => {
          const params = new URLSearchParams(searchParams)
          params.set('sendToCompensation', 'true')
          navigate(
            `${navigation.compensations.incentives.plans.id.fullPath({ id: plan.id })}?${params.toString()}`
          )
        },
        disabled,
      }}
      otherActions={otherActions}
      metadata={[
        {
          label: i18n.t('incentives.plan.recurrence'),
          value: {
            content: recurrenceLabel(plan.recurrence),
            type: 'text',
          },
        },
        {
          label: i18n.t('incentives.plan.start_date'),
          value: {
            formattedDate: selectedPeriod
              ? dateBackendToUI(selectedPeriod.startsOn)
              : dateBackendToUI(plan.startsOn),
            type: 'date',
          },
        },
      ]}
    />
  )
}
